import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useAuth } from "./AuthProvider";
import environment from "../env";
import { useAccount } from "./AccountProvider";
import { useParty } from "./PartyProvider";
import { useUtils } from "./UtilsProvider";

const AnalyticsContext = createContext();

function uuidv4() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4))).toString(16)
  );
}

const AnalyticsProvider = ({ children }) => {
  const { accessToken } = useAuth();
  const [tagInfo, setTagInfo] = useState(null);
  const { useGetAccount } = useAccount();
  const { account } = useGetAccount();
  const { formState } = useUtils();
  const { useGetParty } = useParty();
  const { party } = useGetParty();

  const posting = useRef(false);
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = (() => {
      try {
        const saved = localStorage.getItem("sessionId");
        if (saved) return saved;
        const id = uuidv4();
        try { localStorage.setItem("sessionId", id); } catch {}
        return id;
      } catch {
        return uuidv4();
      }
    })();
  }

  const setTagWithPath = useCallback((tagValue) => {
    const href = (typeof window !== "undefined" && window.location?.href) || "about:blank";
    let path;
    try {
      path = new URL(href).pathname || "/";
    } catch {
      path = (typeof window !== "undefined" && window.location?.pathname) || "/";
    }
    setTagInfo({ tag: tagValue, path, url: href });
  }, []);

  const contextValue = useMemo(
    () => ({ tag: tagInfo?.tag ?? null, setTag: setTagWithPath }),
    [tagInfo?.tag, setTagWithPath]
  );

  const postAnalytics = async () => {
    if (posting.current) return;
    posting.current = true;

    const tagValue = tagInfo?.tag;
    const pathValue = tagInfo?.path;
    const urlValue = tagInfo?.url;

    if (!tagValue || (typeof tagValue === "string" && tagValue.trim() === "")) {
      posting.current = false;
      return;
    }

    if (!urlValue || urlValue === "about:blank") {
      posting.current = false;
      setTagInfo(null);
      return;
    }

    const isLoginFailureTag =
      typeof tagValue === "string" && tagValue.toLowerCase() === "login_idv_failed";

  const accountReference =
  account?.accountReference || formState?.accountReference || "unknown";

let accountId;
let partyId;

if (isLoginFailureTag) {
  accountId = account?.accountId ?? 0;
  partyId = 0;
} else {
  accountId = account?.accountId || 1;
  partyId = party?.partyId || 1;
}

if (!accountReference) {
  posting.current = false;
  return;
}

const analyticsPayload = {
  tag: tagValue,
  accountReference,
  accountId,
  partyId,
  path: pathValue,
  url: urlValue,
  sessionId: sessionIdRef.current,
  client: environment.CLIENT_NAME,
};


    try {
      const response = await fetch(`${environment.CUSTOMER_PORTAL_API}/analytics`, {
        method: "POST",
        headers: {
          "x-api-key": environment.CUSTOMER_PORTAL_API_KEY,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          organisation: environment.CLIENT_NAME,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(analyticsPayload),
      });
      await response.json().catch(() => ({}));
    } catch (error) {
      console.error("Error posting analytics:", error);
    } finally {
      posting.current = false;
      setTagInfo(null);
    }
  };

  useEffect(() => {
    if (!tagInfo) return

    const tagName = typeof tagInfo.tag === "string" ? tagInfo.tag : tagInfo.tag?.tag
    const isFailureTag = tagName?.toLowerCase().includes("_failed")

    const accountReady = account?.accountId && account?.accountReference
    const partyReady = party?.partyId

    if (isFailureTag || (accountReady && partyReady)) {
      if (typeof tagInfo.tag === "object") {
        const { sessionId: _s, client: _c, path: _p, url: _u, ...rest } = tagInfo.tag || {}
        const urlValue = tagInfo.url
        if (!urlValue || urlValue === "about:blank") {
          setTagInfo(null)
          return
        }
        const normalised = {
          ...rest,
          sessionId: sessionIdRef.current,
          client: environment.CLIENT_NAME,
          path: tagInfo.path,
          url: urlValue,
          accountReference:
            rest.accountReference ??
            account?.accountReference ??
            formState?.accountReference ??
            "unknown",
          accountId: rest.accountId ?? (account?.accountId || 1),
          partyId: rest.partyId ?? (party?.partyId || 1),
        }
        fetch(`${environment.CUSTOMER_PORTAL_API}/analytics`, {
          method: "POST",
          headers: {
            "x-api-key": environment.CUSTOMER_PORTAL_API_KEY,
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            organisation: environment.CLIENT_NAME,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(normalised),
        })
          .then(() => setTagInfo(null))
          .catch(() => setTagInfo(null))
      } else {
        postAnalytics()
      }
    }
  }, [tagInfo, account, party, accessToken])

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

AnalyticsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useAnalytics = () => useContext(AnalyticsContext);

export { AnalyticsProvider, useAnalytics };
